import { useState } from 'react';
import { Feld, StatusBadge, formatiereZahl } from '../../muster';
import type { AuslegungslaufWahl } from '../../api';

/**
 * TL1: Der maßgebende Auslegungslauf auf den Seiten Heizlast und Kühllast.
 *
 * Welcher Lauf gilt, entscheidet der Server an EINER Stelle (Auslegungslauf.cs):
 * 1. der als maßgebend gekennzeichnete Lauf,
 * 2. sonst (Heizlast) der jüngste Lauf mit der Auslegungstemperatur des
 *    Leitklimadatensatzes bzw. (Kühllast) der jüngste fertige Lauf,
 * 3. sonst keiner – es gibt KEINEN stillen Rückfall auf den jüngsten Lauf.
 *
 * Diese Bausteine zeigen das nur an und schicken die Kennzeichnung; sie
 * wählen nichts selbst. Anlass: Lauf 46 (θe −10 °C, 14.10.2026) hätte als
 * jüngster Lauf jede Übergabe auf eine Vergleichsrechnung gesetzt.
 */

/** Was die Marke über einen Lauf wissen muss. */
export interface LaufKennung {
  id: string;
  laufnummer: number;
  zweck?: string | null;
  istMassgebend?: boolean;
}

/**
 * Die Marke eines Laufs in der Laufliste: maßgebend (gekennzeichnet),
 * maßgebend (über die Regel), Vergleichslauf oder nichts.
 */
export function Laufmarke({ lauf, wahl }: { lauf: LaufKennung; wahl: AuslegungslaufWahl | null }) {
  if (lauf.istMassgebend) {
    return <StatusBadge zustand="gut" text="maßgebend" titel="als maßgebend gekennzeichnet" klein />;
  }
  if (wahl?.laufId === lauf.id) {
    return (
      <StatusBadge zustand="info" text="maßgebend (Regel)" klein
                   titel={wahl.grundlage === 'auslegungstemperatur'
                     ? 'jüngster Lauf mit der Auslegungstemperatur, nicht gekennzeichnet'
                     : 'jüngster fertiger Lauf, nicht gekennzeichnet'} />
    );
  }
  if (lauf.zweck === 'variante') {
    return <StatusBadge zustand="warnung" text="Vergleichslauf" klein
                        titel="abweichende Außentemperatur – nicht für Auslegung und Übergaben" />;
  }
  return null;
}

/**
 * Der Satz über dem Ergebnis: welcher Lauf angezeigt wird und ob er gilt.
 * Ohne maßgebenden Lauf sagt er, was fehlt.
 */
export function Auslegungshinweis({ art, wahl, gezeigt }: {
  art: 'Heizlast' | 'Kühllast';
  wahl: AuslegungslaufWahl | null;
  gezeigt: LaufKennung | null;
}) {
  if (!wahl) {
    return (
      <p className="schwach" role="status" data-test="auslegungshinweis">
        <StatusBadge zustand="warnung" text="kein maßgebender Lauf" klein />{' '}
        {art === 'Heizlast'
          ? 'Kein Lauf ist als maßgebend gekennzeichnet, und keiner rechnet mit der '
            + 'Auslegungstemperatur des Leitklimadatensatzes. Übergaben an Heizung, Lüftung '
            + 'und Raumbuch finden keinen Lauf.'
          : 'Kein fertiger Kühllastlauf ist vorhanden oder gekennzeichnet.'}
      </p>
    );
  }

  const fremd = gezeigt && gezeigt.id !== wahl.laufId;
  return (
    <p className="schwach" role="status" data-test="auslegungshinweis">
      {fremd
        ? <>
            <StatusBadge zustand="warnung" text={`angezeigt: Lauf ${gezeigt!.laufnummer}`} klein />{' '}
            Dieser Lauf ist <strong>nicht</strong> maßgebend. Maßgebend ist Lauf {wahl.laufnummer}
            {wahl.aussentemperaturC !== null && art === 'Heizlast'
              && <> (θe {formatiereZahl(wahl.aussentemperaturC, 1)} °C)</>}.
          </>
        : <>
            <StatusBadge zustand={wahl.grundlage === 'gekennzeichnet' ? 'gut' : 'info'}
                         text={`maßgebend: Lauf ${wahl.laufnummer}`} klein />{' '}
            {wahl.hinweis}
          </>}
    </p>
  );
}

/**
 * Kennzeichnen mit Begründung. Die Begründung ist Pflicht und steht am Lauf
 * und im Protokoll (audit_log). Kein `window.prompt`: ein beschriftetes Feld.
 */
export function Kennzeichnen({ laufnummer, istMassgebend, beiSenden }: {
  laufnummer: number;
  istMassgebend: boolean;
  /** Schickt die Kennzeichnung; wirft bei Ablehnung mit dem Text des Servers. */
  beiSenden: (begruendung: string, zuruecksetzen: boolean) => Promise<string>;
}) {
  const [offen, setOffen] = useState(false);
  const [begruendung, setBegruendung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const senden = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      setMeldung(await beiSenden(begruendung.trim(), istMassgebend));
      setOffen(false);
      setBegruendung('');
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div>
      {!offen && (
        <button type="button" className="m-knopf" onClick={() => { setOffen(true); setMeldung(null); }}>
          {istMassgebend
            ? `Kennzeichnung von Lauf ${laufnummer} zurücknehmen`
            : `Lauf ${laufnummer} als maßgebend kennzeichnen`}
        </button>
      )}
      {offen && (
        <div className="m-feldgitter">
          <Feld beschriftung={`Begründung (Lauf ${laufnummer})`} art="textfeld" zeilen={2}
                wert={begruendung} pflicht breite="voll"
                hinweis="Steht am Lauf und im Protokoll, z. B. die Antwort des Fachplaners."
                fehler={fehler ?? undefined}
                beiAenderung={(w) => setBegruendung((w as string) ?? '')} />
          <button type="button" className="m-knopf m-knopf-haupt"
                  disabled={laeuft || begruendung.trim() === ''} onClick={() => void senden()}>
            {laeuft ? 'wird gespeichert …' : istMassgebend ? 'zurücknehmen' : 'kennzeichnen'}
          </button>{' '}
          <button type="button" className="m-knopf" disabled={laeuft}
                  onClick={() => { setOffen(false); setFehler(null); }}>
            abbrechen
          </button>
        </div>
      )}
      <span aria-live="polite" className="schwach">{meldung}</span>
    </div>
  );
}
